import { ApiResponse } from "@/api/types";
import { computed, onMounted, ref, watch } from "@vue/composition-api";
import { RouterLocation } from "../router-helper";
import { isNotNullOrUndefined } from "../type-helper";
import { useComponentInstance } from "./useComponentInstance";
import { useCalendarReviewApproved } from "./useCalendarReviewApproved";
import { useAtsApproved } from "./useAtsApproved";
import { useVideoBundleUnlockingLogsApproved } from "./useVideoBundleUnlockingLogsApproved";
import { useCheckinApproved } from "./useCheckinApproved";
import {
  PRIVILEGE_ENTRIES_READ,
  PRIVILEGE_FILES_READ,
  PRIVILEGE_COMPANY_PROPS_WRITE,
  hasPrivilege,
  PRIVILEGE_SCOUT
} from "../privilege-helper";

export type MenuConfig = {
  name: string;
  iconName: string;
  to?: RouterLocation;
  onClick?: () => void;
  subMenus?: SubMenuConfig[];
};

type SubMenuMode = "accordion" | "section";

export type SubMenuConfig = {
  name?: string | null;
  to?: RouterLocation;
  onClick?: () => void;
  subMenus?: {
    name: string;
    to?: RouterLocation;
    onClick?: () => void;
  }[];
  mode?: SubMenuMode; // subMenusがある場合の表示形式
};

export const useOccNavigation = () => {
  const root = useComponentInstance();
  const { isCalendarReviewApproved } = useCalendarReviewApproved();
  const { isCheckinApproved } = useCheckinApproved();
  const { isAtsApproved } = useAtsApproved();
  const {
    isVideoBundleUnlockingLogsApproved
  } = useVideoBundleUnlockingLogsApproved();

  const loadingGetPlans = ref(false);

  const isAdminPartnerUser = computed<boolean>(() => {
    const currentPartnerUser = root.$store.state.domains.user.partnerUser;
    return !!currentPartnerUser?.admin;
  });

  const isLoggedInByProxy = computed<boolean>(() => {
    return root.$store.state.domains.user.partnerUser?.logged_in_by_proxy;
  });

  // TODO: エージェント用のメニューは画面を追加する際に実装する
  const agentMenuConfigs = computed<MenuConfig[]>(() =>
    [
      {
        name: "スカウト",
        iconName: "renewal/scout",
        onClick: () => {},
        subMenus: [
          {
            name: "スカウト候補者一覧",
            onClick: () => {},
            to: { name: "ScoutCandidates" }
          },
          (!isLoggedInByProxy.value || undefined) && {
            name: "メッセージ",
            onClick: () => {},
            to: { name: "ScoutMessages" }
          },
          {
            name: "テンプレート",
            onClick: () => {},
            to: { name: "ScoutMessageTemplates" }
          },
          {
            name: "スカウト受諾者一覧",
            onClick: () => {},
            to: { name: "AgentScoutAcceptances" }
          }
        ].filter(isNotNullOrUndefined)
      },
      (hasPrivilege(PRIVILEGE_FILES_READ) || undefined) && {
        name: "ファイル",
        iconName: "renewal/file",
        onClick: () => {},
        to: { name: "Files" }
      }
    ].filter(isNotNullOrUndefined)
  );

  const defaultMenuConfigs = computed<MenuConfig[]>(() =>
    [
      {
        name: "ホーム",
        iconName: "renewal/home",
        onClick: () => {},
        to: { name: "Home" }
      },
      (isCalendarReviewApproved.value || undefined) && {
        name: "ランキング",
        iconName: "renewal/crown",
        onClick: () => {},
        to: { name: "RankingsFavorites" }
      },
      {
        name: "求人掲載",
        iconName: "renewal/office",
        onClick: () => {},
        subMenus: [
          {
            name: "企業ページ",
            onClick: () => {},
            to: { name: "OfficialEdit" }
          },
          {
            name: "イベント一覧",
            onClick: () => {},
            to: { name: "JobEvents" }
          },
          (isCheckinApproved.value || undefined) && {
            name: "ワンキャリアイベント一覧",
            onClick: () => {},
            to: { name: "SponsoredEvents" }
          }
        ].filter(isNotNullOrUndefined)
      },
      (hasPrivilege(PRIVILEGE_SCOUT) || undefined) && {
        name: "スカウト",
        iconName: "renewal/scout",
        onClick: () => {},
        subMenus: [
          {
            name: "スカウト候補者一覧",
            onClick: () => {},
            to: { name: "ScoutCandidates" }
          },
          (!isLoggedInByProxy.value || undefined) && {
            name: "メッセージ",
            onClick: () => {},
            to: { name: "ScoutMessages" }
          },
          {
            name: "テンプレート",
            onClick: () => {},
            to: { name: "ScoutMessageTemplates" }
          }
        ].filter(isNotNullOrUndefined)
      },
      (hasPrivilege(PRIVILEGE_ENTRIES_READ) || undefined) && {
        name: "応募者一覧",
        iconName: "renewal/users",
        onClick: () => {},
        subMenus: [
          {
            name: "応募者一覧",
            onClick: () => {},
            to: { name: "Entries" }
          },
          (isVideoBundleUnlockingLogsApproved.value || undefined) && {
            name: "動画エントリー一覧",
            onClick: () => {},
            to: { name: "VideoBundleUnlockingLogs" }
          },
          (isCheckinApproved.value || undefined) && {
            name: "オンラインイベント",
            onClick: () => {},
            subMenus: [
              {
                name: "合説 チェックイン",
                onClick: () => {},
                to: { name: "SponsoredEventEntryCheckins" }
              },
              {
                name: "企業説明会申込者",
                onClick: () => {},
                to: { name: "SponsoredEventEntryApplicants" }
              },
              {
                name: "座談会出席者",
                onClick: () => {},
                to: { name: "OnlineRoundtable" }
              }
            ],
            mode: "section" as SubMenuMode
          },
          (isCheckinApproved.value || undefined) && {
            name: "オフラインイベント",
            onClick: () => {},
            subMenus: [
              {
                name: "合説 ブースチェックイン",
                onClick: () => {},
                to: { name: "OfflineCheckin" }
              },
              {
                name: "合説 特別講演出席者",
                onClick: () => {},
                to: { name: "OfflineCourse" }
              },
              {
                name: "座談会出席者",
                onClick: () => {},
                to: { name: "OfflineRoundtable" }
              }
            ],
            mode: "section" as SubMenuMode
          }
        ].filter(isNotNullOrUndefined)
      },
      (isCalendarReviewApproved.value || undefined) && {
        name: "クチコミ",
        iconName: "renewal/comment",
        onClick: () => {},
        subMenus: [
          {
            name: "自社のクチコミ",
            onClick: () => {},
            to: {
              name: "CompanyDetail",
              params: {
                companyId: String(partnerUser.value?.partner.company.id)
              }
            }
          },
          {
            name: "他社のクチコミ",
            onClick: () => {},
            to: { name: "Companies" }
          },
          {
            name: "クチコミを集める",
            onClick: () => {},
            to: { name: "ReviewOffers" }
          }
        ]
      },
      (isCalendarReviewApproved.value || undefined) && {
        name: "カレンダー",
        iconName: "renewal/calendar",
        onClick: () => {},
        subMenus: [
          ...(Array.isArray(plans.value)
            ? plans.value.map(plan => ({
                name: plan.name,
                to: { name: "Plan", params: { planId: String(plan.id) } }
              }))
            : []),
          {
            name: "全部を表示",
            onClick: () => {},
            to: { name: "Plans" }
          }
        ]
      },
      {
        name: "パフォーマンス",
        iconName: "renewal/chart",
        onClick: () => {},
        subMenus: [
          {
            name: "企業ページ",
            onClick: () => {},
            to: { name: "PerformancesOfficial" }
          },
          {
            name: "イベントページ",
            onClick: () => {},
            to: { name: "PerformancesEvents" }
          }
          // スカウトパフォーマンスレポートにバグがあるので暫定的にサイドメニューからも除外する。対応時期は未定
          // {
          //   name: "スカウト",
          //   onClick: () => {},
          //   to: { name: "PerformancesScout" }
          // }
        ]
      },
      (hasPrivilege(PRIVILEGE_FILES_READ) || undefined) && {
        name: "ファイル",
        iconName: "renewal/file",
        onClick: () => {},
        to: { name: "Files" }
      },
      {
        name: "設定",
        iconName: "renewal/settings",
        subMenus: [
          (hasPrivilege(PRIVILEGE_COMPANY_PROPS_WRITE) || undefined) && {
            name: "企業ロゴの設定",
            to: {
              name: "AccountsCompanyLogo"
            }
          },
          // NOTE: 画面未実装
          // {
          //   name: "パスワードの設定",
          //   to: {
          //     name: "EditPassword"
          //   }
          // },
          (isAdminPartnerUser.value || undefined) && {
            name: "ユーザー管理",
            to: {
              name: "AccountsPartnerUsers"
            }
          },
          (isAdminPartnerUser.value || undefined) && {
            name: "権限管理",
            to: {
              name: "AccountsPrivileges"
            }
          },
          {
            name: "通知設定",
            to: {
              name: "AccountsEditNotification"
            }
          },
          (isCalendarReviewApproved.value || undefined) && {
            name: "フォロー設定",
            subMenus: [
              {
                name: "企業",
                to: {
                  name: "AccountsFollowCampanies"
                }
              },
              {
                name: " 大学",
                to: {
                  name: "AccountsFollowUniversities"
                }
              }
            ],
            mode: "accordion" as SubMenuMode
          },
          (isAtsApproved.value || undefined) && {
            name: "外部連携",
            subMenus: [
              {
                name: "i-web",
                to: {
                  name: "AccountsIntegrationsIweb"
                }
              },
              {
                name: " SONAR",
                to: {
                  name: "AccountsIntegrationsSonar"
                }
              }
            ],
            mode: "accordion" as SubMenuMode
          },
          {
            name: "契約一覧",
            to: {
              name: "AccountsContractInformations"
            }
          }
        ].filter(isNotNullOrUndefined)
      }
    ].filter(isNotNullOrUndefined)
  );

  const menuConfigs = computed<MenuConfig[]>(() => {
    if (root.$store.getters["domains/user/isScoutAgentMenuActive"]) {
      return agentMenuConfigs.value;
    }

    // エージェントメニューがアクティブでない場合はデフォルトを返却する
    return defaultMenuConfigs.value;
  });

  const isLoggedIn = computed<ApiResponse.PartnerUser | undefined>(
    () => root.$store.getters["domains/user/isLoggedIn"]
  );

  const partnerUser = computed<ApiResponse.PartnerUser | undefined>(
    () => root.$store.state.domains.user.partnerUser
  );

  const plans = computed<ApiResponse.Plan[]>(() => {
    const plans = root.$store.state.domains.plans.plans;
    if (loadingGetPlans.value || !plans) {
      return [];
    }

    return plans.slice(0, 5);
  });

  const getPlans = async () => {
    if (!partnerUser.value) return;
    loadingGetPlans.value = true;
    await root.$store.dispatch("domains/plans/getPlans", {
      partnerId: partnerUser.value.partner.id
    });
    loadingGetPlans.value = false;
  };

  watch(
    () => isLoggedIn,
    currentIsLoggedIn => currentIsLoggedIn && getPlans()
  );

  onMounted(() => {
    getPlans();
  });

  return { menuConfigs, getPlans } as const;
};
